<template>
  <div class="concessions-page">
    <div class="page-header">
      <div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-hand-holding-dollar"></i>
        </div>
        <div>
          <h1 class="page-title">Concessions</h1>
          <p class="page-subtitle">Price exception requests and approvals</p>
        </div>
      </div>

      <div class="header-actions">
        <select v-if="tab === 'dashboard'" v-model="days" class="filter-select days-select" @change="loadRequests">
          <option :value="7">Last 7 Days</option>
          <option :value="30">Last 30 Days</option>
          <option :value="90">Last 90 Days</option>
          <option :value="365">Last Year</option>
        </select>

        <!-- The roster and ceilings this imports are concession data, so the importer lives
             with them rather than on the Lock Desk Queue page it started on. -->
        <button v-if="context.is_concessions_admin"
                class="approvers-btn"
                @click="openApproverImport"
                title="Import Concession Approvers">
          <i class="fa-duotone fa-file-import"></i>
        </button>

        <button class="refresh-btn" :disabled="loading" @click="reload">
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <!-- Request / Dashboard switch. The dashboard only exists for approvers and
         lock desk staff; everyone else just sees their own history there. -->
    <div class="tab-bar">
      <button class="tab-btn" :class="{ 'is-active': tab === 'request' }" @click="tab = 'request'">
        <i class="fa-duotone fa-file-circle-plus"></i> Request
      </button>
      <button class="tab-btn" :class="{ 'is-active': tab === 'dashboard' }" @click="switchToDashboard">
        <i class="fa-duotone fa-table-rows"></i>
        {{ context.is_concessions_admin
             ? 'All Concessions'
             : (context.is_approver ? 'Approvals' : 'My Requests') }}
        <span v-if="pendingForMe.length" class="tab-badge">{{ pendingForMe.length }}</span>
      </button>
      <button class="tab-btn" :class="{ 'is-active': tab === 'audit' }" @click="switchToAudit">
        <i class="fa-duotone fa-clipboard-list-check"></i> Audit
      </button>
      <!-- Admin only: shows routing and recipient addresses across every division. -->
      <button v-if="context.is_concessions_admin"
              class="tab-btn" :class="{ 'is-active': tab === 'diagnostics' }"
              @click="switchToDiagnostics">
        <i class="fa-duotone fa-stethoscope"></i> Polly Intake
        <span v-if="pollyDryRunCount" class="tab-badge">{{ pollyDryRunCount }}</span>
      </button>
    </div>

    <div v-if="contextLoading" class="state-card">Loading your access...</div>

    <!-- ══════════════════════════ REQUEST ══════════════════════════ -->
    <template v-else-if="tab === 'request'">
      <!-- The form is NOT gated — anyone signed in may file one, and the
           approvers are the gate. roster_note is informational: it says what
           will happen to what you file, it does not stop you filing it. -->
      <div v-if="context.roster_note" class="roster-note-card">
        <div class="roster-note-icon"><i class="fa-duotone fa-circle-info"></i></div>
        <div class="roster-note-text">{{ context.roster_note }}</div>
      </div>

      <div class="request-card">
        <div class="requester-line">
          Filing as <strong>{{ context.email }}</strong>
          <span v-if="context.division"> &middot; {{ context.division }}</span>
          <span v-if="context.branch_name"> &middot; {{ context.branch_name }}</span>
          <div class="requester-sub">
            The concession is raised under the loan's officer, and their ceilings and
            approvers are what apply — not yours.
          </div>
        </div>

        <div class="form-grid">
          <label class="form-field">
            <span class="form-label">Loan number</span>
            <input v-model.trim="form.loan_number" class="form-input" type="text" placeholder="Loan number" />
          </label>

          <label class="form-field">
            <span class="form-label">Loan ID <span class="req">*</span></span>
            <input v-model.trim="form.loan_id" class="form-input" type="text"
                   placeholder="Encompass loan GUID" />
            <span class="form-hint">Polly needs this to attach the price exception.</span>
          </label>

          <label class="form-field">
            <span class="form-label">Borrower first</span>
            <input v-model.trim="form.borrower_first" class="form-input" type="text" />
          </label>

          <label class="form-field">
            <span class="form-label">Borrower last</span>
            <input v-model.trim="form.borrower_last" class="form-input" type="text" />
          </label>

          <label class="form-field">
            <span class="form-label">Loan amount</span>
            <input v-model.number="form.loan_amount" class="form-input" type="number" step="0.01" min="0" />
            <span class="form-hint">Used to convert between bps and dollars.</span>
          </label>

          <label class="form-field">
            <span class="form-label">Product <span class="req">*</span></span>
            <select v-model="form.product_type" class="form-input">
              <option v-for="p in context.products" :key="p" :value="p">{{ productLabel(p) }}</option>
            </select>
          </label>

          <label class="form-field">
            <span class="form-label">Paid by <span class="req">*</span></span>
            <select v-model="form.tier" class="form-input" @change="onTierChange">
              <option value="branch">Branch</option>
              <option value="corp">Corporate</option>
            </select>
          </label>

          <label class="form-field">
            <span class="form-label">Reason <span class="req">*</span></span>
            <select v-model="form.reason_code" class="form-input">
              <option value="">Select a reason...</option>
              <option v-for="r in reasonsForTier" :key="r.id || r.code" :value="r.code">{{ r.label }}</option>
            </select>
            <span v-if="!reasonsForTier.length" class="form-hint is-warn">
              No reasons available — the Polly reason sync hasn't run yet.
            </span>
            <span v-else-if="selectedReasonRequiresNote" class="form-hint">This reason requires a note.</span>
          </label>
        </div>

        <div class="amount-row">
          <label class="form-field">
            <span class="form-label">Amount (bps)</span>
            <input v-model.number="form.requested_bps" class="form-input" type="number" step="0.01" min="0"
                   @input="form.requested_amount = null" />
          </label>

          <div class="amount-or">or</div>

          <label class="form-field">
            <span class="form-label">Amount ($)</span>
            <input v-model.number="form.requested_amount" class="form-input" type="number" step="0.01" min="0"
                   @input="form.requested_bps = null" />
          </label>

          <div v-if="derivedAmountLabel" class="amount-derived">{{ derivedAmountLabel }}</div>
        </div>

        <label class="form-field full">
          <span class="form-label">
            Notes <span v-if="selectedReasonRequiresNote" class="req">*</span>
          </span>
          <textarea v-model.trim="form.requester_notes" class="form-input form-textarea" rows="3"
                    placeholder="Anything the approver should know..."></textarea>
        </label>

        <div v-if="submitError" class="inline-error">{{ submitError }}</div>

        <!-- Outcome. The server decides; this only reports what it said.
             isPreview distinguishes a dry run from a filed request — a preview
             wrote nothing, so it must not read like a confirmation. -->
        <div v-if="submitResult" class="outcome-card"
             :class="isPreview ? 'is-preview' : (submitResult.auto_approved ? 'is-approved' : 'is-pending')">
          <div class="outcome-head">
            <i :class="isPreview
                       ? 'fa-duotone fa-eye'
                       : (submitResult.auto_approved
                          ? 'fa-duotone fa-circle-check'
                          : 'fa-duotone fa-user-clock')"></i>
            <strong v-if="isPreview">
              Preview — nothing saved.
              {{ submitResult.auto_approved ? 'This would auto-approve' : 'This would need approval' }}
            </strong>
            <strong v-else>{{ submitResult.auto_approved ? 'Auto-approved' : 'Sent for approval' }}</strong>
          </div>

          <!-- Polly's before/after, which only a preview returns. -->
          <div v-if="submitResult.polly && (submitResult.polly.prior_final_price !== null &&
                                            submitResult.polly.prior_final_price !== undefined)"
               class="outcome-pricing">
            <span>Price before: <strong>{{ formatPrice(submitResult.polly.prior_final_price) }}</strong></span>
            <span>after: <strong>{{ formatPrice(submitResult.polly.exception_final_price) }}</strong></span>
            <span v-if="priceDelta !== null">
              change: <strong>{{ priceDelta > 0 ? '+' : '' }}{{ formatPrice(priceDelta) }}</strong>
            </span>
          </div>
          <div class="outcome-text">{{ submitResult.explanation }}</div>
          <div v-if="submitResult.routed_to && submitResult.routed_to.length" class="outcome-text">
            Waiting on {{ submitResult.routed_to.join(', ') }}.
          </div>
          <div v-else-if="!submitResult.auto_approved" class="outcome-text">
            No approvers are configured for your division yet, so the lock desk will review it.
          </div>

          <!-- Polly is the system of record, so say plainly whether it landed. -->
          <div v-if="submitResult.polly_error" class="outcome-text is-warn">
            <i class="fa-duotone fa-triangle-exclamation"></i>
            Recorded here, but Polly hasn't accepted it yet: {{ submitResult.polly_error }}
          </div>
          <div v-else-if="submitResult.polly && submitResult.polly.pe_request_id" class="outcome-text is-ok">
            Polly price exception created ({{ submitResult.polly.pe_request_id }}).
          </div>
        </div>

        <div class="form-actions">
          <button class="cancel-btn" :disabled="submitting" @click="resetForm">Clear</button>
          <button class="preview-btn" :disabled="submitting || !canSubmit" @click="preview">
            {{ previewing ? 'Checking...' : 'Preview' }}
          </button>
          <button class="save-btn" :disabled="submitting || !canSubmit" @click="submit">
            {{ submitting && !previewing ? 'Submitting...' : 'Submit concession' }}
          </button>
        </div>
      </div>
    </template>

    <!-- ══════════════════════════ DASHBOARD ══════════════════════════ -->
    <template v-else-if="tab === 'dashboard'">
      <div class="summary-cards">
        <div class="summary-card">
          <div class="summary-value">{{ requests.length }}</div>
          <div class="summary-label">Total</div>
        </div>
        <div class="summary-card is-pending">
          <div class="summary-value">{{ countBy('pending') }}</div>
          <div class="summary-label">Pending</div>
        </div>
        <div class="summary-card is-approved">
          <div class="summary-value">{{ countBy('auto_approved') + countBy('approved') }}</div>
          <div class="summary-label">Approved</div>
        </div>
        <div class="summary-card is-rejected">
          <div class="summary-value">{{ countBy('rejected') }}</div>
          <div class="summary-label">Rejected</div>
        </div>
        <div class="summary-card">
          <div class="summary-value">{{ avgBps }}</div>
          <div class="summary-label">Avg bps</div>
        </div>
        <div class="summary-card">
          <div class="summary-value">{{ totalDollars }}</div>
          <div class="summary-label">Total given</div>
        </div>
        <div class="summary-card is-warn" v-if="pollyProblems.length">
          <div class="summary-value">{{ pollyProblems.length }}</div>
          <div class="summary-label">Not in Polly</div>
        </div>
      </div>

      <div class="filter-row">
        <select v-model="statusFilter" class="filter-select" @change="loadRequests">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="auto_approved">Auto-approved</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <select v-if="filterDivisions.length > 1" v-model="divisionFilter"
                class="filter-select" @change="loadRequests">
          <option value="">{{ context.is_concessions_admin ? 'All divisions' : 'All my divisions' }}</option>
          <option v-for="d in filterDivisions" :key="d" :value="d">{{ d }}</option>
        </select>
      </div>

      <div v-if="pollyProblems.length" class="warn-banner">
        <i class="fa-duotone fa-triangle-exclamation"></i>
        <span>
          {{ pollyProblems.length }}
          {{ pollyProblems.length === 1 ? 'concession is' : 'concessions are' }}
          approved here but not yet accepted by Polly. They are recorded, but the price exception
          does not exist on the loan until Polly takes it.
        </span>
      </div>

      <div v-if="loading" class="state-card">Loading...</div>
      <div v-else-if="!requests.length" class="state-card">No concessions in this window.</div>

      <div v-else class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>Requested</th>
              <th>Loan</th>
              <th>Borrower</th>
              <th>Requested by</th>
              <th>Division</th>
              <th>Paid by</th>
              <th>Product</th>
              <th class="num">bps</th>
              <th class="num">$</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Polly</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="r in requests" :key="r.id">
              <tr>
                <td class="nowrap">{{ formatDateTime(r.created_utc) }}</td>
                <td class="nowrap">{{ r.loan_number || '—' }}</td>
                <td>{{ borrowerName(r) }}</td>
                <td class="wrap-email">{{ r.requested_by_name || r.requested_by_email }}</td>
                <td>{{ r.division || '—' }}</td>
                <td>{{ r.tier === 'corp' ? 'Corporate' : 'Branch' }}</td>
                <td>{{ productLabel(r.product_type) }}</td>
                <td class="num">{{ formatNumber(r.requested_bps) }}</td>
                <td class="num">{{ formatMoney(r.requested_amount) }}</td>
                <td>{{ r.reason_text || r.reason_code || '—' }}</td>
                <td>
                  <span class="pill" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span>
                  <div v-if="r.decided_by_name" class="sub-note">by {{ r.decided_by_name }}</div>
                </td>
                <td>
                  <span class="pill" :class="pollyClass(r.polly_sync_status)">
                    {{ pollyLabel(r.polly_sync_status) }}
                  </span>
                </td>
                <td class="actions-cell">
                  <button class="link-btn" @click="toggleDetail(r)">
                    {{ expandedId === r.id ? 'Hide' : 'Details' }}
                  </button>
                  <button v-if="r.status === 'pending'" class="approve-btn"
                          :disabled="decidingId === r.id" @click="openDecision(r, 'approve')">
                    Approve
                  </button>
                  <button v-if="r.status === 'pending'" class="reject-btn"
                          :disabled="decidingId === r.id" @click="openDecision(r, 'reject')">
                    Reject
                  </button>
                </td>
              </tr>

              <tr v-if="expandedId === r.id" class="detail-row">
                <td colspan="13">
                  <div class="detail-grid">
                    <div>
                      <div class="detail-title">Decision</div>
                      <div class="detail-line">Rule: <code>{{ r.matched_rule || '—' }}</code></div>
                      <div class="detail-line">
                        Ceiling applied:
                        {{ r.threshold_bps_applied === null || r.threshold_bps_applied === undefined
                           ? 'none configured (auto-approve)'
                           : formatNumber(r.threshold_bps_applied) + ' bps' }}
                      </div>
                      <div class="detail-line">Routing: {{ routingLabel(r.routing) }}</div>
                      <div v-if="r.decision_notes" class="detail-line">Notes: {{ r.decision_notes }}</div>
                    </div>
                    <div>
                      <div class="detail-title">Polly</div>
                      <div class="detail-line">Status: {{ pollyLabel(r.polly_sync_status) }}</div>
                      <div v-if="r.polly_pe_request_id" class="detail-line">
                        peRequestId: <code>{{ r.polly_pe_request_id }}</code>
                      </div>
                      <div v-if="r.polly_sync_attempts" class="detail-line">
                        Attempts: {{ r.polly_sync_attempts }}
                      </div>
                      <div v-if="r.polly_sync_last_error" class="detail-line is-warn">
                        {{ r.polly_sync_last_error }}
                      </div>
                    </div>
                    <div>
                      <div class="detail-title">Requester notes</div>
                      <div class="detail-line">{{ r.requester_notes || '—' }}</div>
                    </div>
                    <div>
                      <div class="detail-title">Audit trail</div>
                      <div v-if="eventsLoading" class="detail-line">Loading...</div>
                      <div v-else-if="!events.length" class="detail-line">No events.</div>
                      <div v-for="e in events" :key="e.id" class="detail-line">
                        <span class="event-when">{{ formatDateTime(e.created_utc, true) }}</span>
                        <strong>{{ eventLabel(e.event_type) }}</strong>
                        <span v-if="e.actor_name || e.actor_email">
                          — {{ e.actor_name || e.actor_email }}
                        </span>
                        <div v-if="e.notes" class="event-notes">{{ e.notes }}</div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <!-- ══════════════════════════ AUDIT ══════════════════════════ -->
    <!-- Every recorded action and who took it. Scoped server-side, so this shows
         exactly what the signed-in user is allowed to see and nothing more. -->
    <!-- ═══════════════════ POLLY INTAKE DIAGNOSTICS ═══════════════════ -->
    <template v-else-if="tab === 'diagnostics'">
      <div class="filter-row">
        <!-- Only the day window re-queries; everything else filters the loaded
             rows, so sifting is instant. -->
        <select v-model="pollyDays" class="filter-select" @change="loadPollyEvents">
          <option :value="1">Last 24 Hours</option>
          <option :value="7">Last 7 Days</option>
          <option :value="14">Last 14 Days</option>
          <option :value="90">Last 90 Days</option>
        </select>

        <button class="refresh-btn" :disabled="pollyLoading" @click="loadPollyEvents">
          {{ pollyLoading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>

      <div class="filter-row">
        <!-- The flag filter first: it is what you actually reach for, because
             each option is a distinct reason the pipeline declined to act. -->
        <select v-model="pollyFilters.flag" class="filter-select">
          <option value="">Any outcome flag</option>
          <option value="no_owner">Not on access list</option>
          <option value="reason_unmatched">Reason unmatched</option>
          <option value="bypassed">Bypassed internal approval</option>
          <option value="emailed">Emails sent</option>
          <option value="clean">No flags</option>
        </select>

        <select v-model="pollyFilters.mode" class="filter-select">
          <option value="">Any mode</option>
          <option value="dry">Dry</option>
          <option value="live">Live</option>
        </select>

        <select v-model="pollyFilters.action" class="filter-select">
          <option value="">Any outcome</option>
          <option v-for="a in pollyFacets.actions" :key="a" :value="a">{{ a }}</option>
        </select>

        <select v-model="pollyFilters.event" class="filter-select">
          <option value="">Any event</option>
          <option v-for="e in pollyFacets.events" :key="e" :value="e">{{ e }}</option>
        </select>

        <select v-if="pollyFacets.divisions.length > 1" v-model="pollyFilters.division" class="filter-select">
          <option value="">Any division</option>
          <option v-for="d in pollyFacets.divisions" :key="d" :value="d">{{ d }}</option>
        </select>

        <select v-if="pollyFacets.tiers.length > 1" v-model="pollyFilters.tier" class="filter-select">
          <option value="">Any tier</option>
          <option v-for="t in pollyFacets.tiers" :key="t" :value="t">{{ t }}</option>
        </select>

        <select v-model="pollyFilters.raisedBy" class="filter-select">
          <option value="">Any loan officer</option>
          <option v-for="r in pollyFacets.raisedBy" :key="r" :value="r">{{ r }}</option>
        </select>

        <select v-if="pollyFacets.pollyUsers.length > 1" v-model="pollyFilters.pollyUser"
                class="filter-select">
          <option value="">Any Polly user</option>
          <option v-for="u in pollyFacets.pollyUsers" :key="u" :value="u">{{ u }}</option>
        </select>

        <input v-model.trim="pollyFilters.loan" class="filter-select audit-search"
               type="text" placeholder="Loan number..." />

        <button v-if="pollyFiltersActive" class="link-btn" @click="clearPollyFilters">
          Clear filters
        </button>
      </div>

      <div class="diag-explainer">
        Every price exception Polly sent us, and what the concession pipeline made of it.
        <strong>Dry</strong> rows were measured only — nothing was filed and nobody was emailed —
        which is what you get while the intake switch is off.
      </div>

      <div v-if="pollyError" class="blocked-card">
        <div class="blocked-icon"><i class="fa-duotone fa-circle-exclamation"></i></div>
        <div>
          <div class="blocked-title">Couldn't load the diagnostics</div>
          <div class="blocked-text">{{ pollyError }}</div>
        </div>
      </div>

      <div class="audit-count">
        <span v-if="pollyLoading">Loading events...</span>
        <span v-else-if="!pollyEvents.length">
          No Polly price-exception events have reached the pipeline yet.
        </span>
        <span v-else-if="pollyFiltersActive">
          Showing {{ filteredPollyEvents.length }} of {{ pollyEvents.length }}
          event{{ pollyEvents.length === 1 ? '' : 's' }}
        </span>
        <span v-else>
          {{ pollyEvents.length }} event{{ pollyEvents.length === 1 ? '' : 's' }},
          {{ pollyDryRunCount }} measured only
        </span>
      </div>

      <div v-if="!pollyLoading && pollyEvents.length && !filteredPollyEvents.length" class="state-card">
        No events match these filters.
        <button class="link-btn" @click="clearPollyFilters">Clear filters</button>
      </div>

      <div v-if="filteredPollyEvents.length" class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Mode</th>
              <th>Event</th>
              <th>Loan</th>
              <th>Raised by (loan officer)</th>
              <th>Tier / Product</th>
              <th class="num">Points</th>
              <th class="num">bps</th>
              <th class="num">Ceiling</th>
              <th>Rule</th>
              <th>Outcome</th>
              <th>Would email</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filteredPollyEvents" :key="row.id">
              <td class="nowrap">{{ formatDateTime(row.created_utc) }}</td>
              <td>
                <span class="mode-pill" :class="row.dry_run ? 'is-dry' : 'is-live'">
                  {{ row.dry_run ? 'Dry' : 'Live' }}
                </span>
              </td>
              <td class="nowrap">{{ row.polly_event || '—' }}</td>
              <td class="nowrap">{{ row.loan_number || '—' }}</td>
              <!-- Raised by = the loan's OFFICER, matching how a concession filed in
                   Swift is attributed. The Polly user who actually pressed the button
                   is shown beneath when it is someone else. -->
              <td>
                {{ row.requested_by_email || '—' }}
                <!-- No owner means no ceilings and no approvers could be resolved, which is
                     the single most common reason nothing gets filed. -->
                <span v-if="row.requested_by_email && !row.owner_resolved" class="diag-flag is-warn">
                  not on access list
                </span>
                <div v-if="row.division" class="diag-sub">{{ row.division }}</div>
                <div v-if="row.polly_user_email && row.polly_user_email !== row.requested_by_email"
                     class="diag-sub">
                  via {{ row.polly_user_email }}
                </div>
              </td>
              <td>
                {{ row.tier || '—' }}
                <span v-if="row.reason_code && !row.reason_matched" class="diag-flag is-warn">
                  reason unmatched
                </span>
                <div v-if="row.product_type" class="diag-sub">{{ row.product_type }}</div>
              </td>
              <td class="num">{{ row.exception_points != null ? Number(row.exception_points).toFixed(3) : '—' }}</td>
              <td class="num">{{ row.requested_bps != null ? Number(row.requested_bps).toFixed(2) : '—' }}</td>
              <td class="num">{{ row.threshold_bps != null ? Number(row.threshold_bps).toFixed(2) : '—' }}</td>
              <td>
                {{ row.matched_rule || '—' }}
                <div v-if="row.routing" class="diag-sub">{{ row.routing }}</div>
              </td>
              <td>
                <span class="diag-action">{{ row.action || '—' }}</span>
                <div v-if="row.status" class="diag-sub">{{ row.status }}</div>
                <span v-if="row.internal_approval_bypassed" class="diag-flag is-warn">
                  bypassed internal approval
                </span>
              </td>
              <td>
                <div v-if="row.would_email_approvers" class="diag-sub">
                  approvers: {{ row.would_email_approvers }}
                </div>
                <div v-if="row.would_email_branch_manager" class="diag-sub">
                  branch: {{ row.would_email_branch_manager }}
                </div>
                <span v-if="!row.would_email_approvers && !row.would_email_branch_manager">—</span>
                <span v-if="row.emails_sent" class="diag-flag is-sent">sent</span>
              </td>
              <td class="diag-message">{{ row.message || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template v-else-if="tab === 'audit'">
      <div class="filter-row">
        <select v-model="audit.days" class="filter-select" @change="loadAudit(0)">
          <option :value="7">Last 7 Days</option>
          <option :value="30">Last 30 Days</option>
          <option :value="90">Last 90 Days</option>
          <option :value="365">Last Year</option>
          <option :value="0">All time</option>
        </select>

        <select v-model="audit.event_type" class="filter-select" @change="loadAudit(0)">
          <option value="">All actions</option>
          <option v-for="t in auditPage.event_types" :key="t" :value="t">{{ eventLabel(t) }}</option>
        </select>

        <select v-model="audit.actor_email" class="filter-select" @change="loadAudit(0)">
          <option value="">Anyone</option>
          <option v-for="a in auditPage.actors" :key="a" :value="a">{{ a }}</option>
        </select>

        <select v-if="filterDivisions.length > 1" v-model="audit.division"
                class="filter-select" @change="loadAudit(0)">
          <option value="">{{ context.is_concessions_admin ? 'All divisions' : 'All my divisions' }}</option>
          <option v-for="d in filterDivisions" :key="d" :value="d">{{ d }}</option>
        </select>

        <input v-model.trim="audit.loan_number" class="filter-select audit-search"
               type="text" placeholder="Loan number..." @keyup.enter="loadAudit(0)" />

        <button class="refresh-btn" :disabled="auditLoading" @click="loadAudit(0)">
          {{ auditLoading ? 'Loading...' : 'Apply' }}
        </button>
      </div>

      <div class="audit-count">
        <span v-if="auditLoading">Loading actions...</span>
        <span v-else-if="!auditPage.total">No actions recorded for this filter.</span>
        <span v-else>
          Showing {{ auditPage.entries.length }} of {{ auditPage.total }}
          action{{ auditPage.total === 1 ? '' : 's' }}
        </span>
      </div>

      <div v-if="auditPage.entries.length" class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Who</th>
              <th>Role</th>
              <th>Loan</th>
              <th>Division</th>
              <th>Paid by</th>
              <th class="num">bps</th>
              <th>Change</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="e in auditPage.entries" :key="e.id">
              <td class="nowrap">{{ formatDateTime(e.created_utc, true) }}</td>
              <td>
                <span class="pill" :class="auditEventClass(e.event_type)">{{ eventLabel(e.event_type) }}</span>
              </td>
              <td class="wrap-email">
                {{ e.actor_name || e.actor_email || 'System' }}
                <div v-if="e.actor_name && e.actor_email" class="sub-note">{{ e.actor_email }}</div>
              </td>
              <td>{{ e.actor_role || '—' }}</td>
              <td class="nowrap">
                {{ e.loan_number || '—' }}
                <div v-if="borrowerName(e) !== '—'" class="sub-note">{{ borrowerName(e) }}</div>
              </td>
              <td>{{ e.division || '—' }}</td>
              <td>{{ e.tier === 'corp' ? 'Corporate' : 'Branch' }}</td>
              <td class="num">{{ formatNumber(e.requested_bps) }}</td>
              <td class="nowrap">
                <span v-if="e.from_status || e.to_status">
                  {{ e.from_status ? statusLabel(e.from_status) : '—' }} &rarr;
                  {{ e.to_status ? statusLabel(e.to_status) : '—' }}
                </span>
                <span v-else>—</span>
              </td>
              <td>
                <div v-if="e.notes" class="audit-notes">{{ e.notes }}</div>
                <div v-if="e.reason_text" class="sub-note">{{ e.reason_text }}</div>
                <div v-if="!e.notes && !e.reason_text" class="sub-note">—</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="auditPage.total > auditPage.take" class="audit-pager">
        <button class="link-btn" :disabled="auditLoading || auditPage.skip <= 0"
                @click="loadAudit(Math.max(0, auditPage.skip - auditPage.take))">
          Newer
        </button>
        <span class="sub-note">
          {{ auditPage.skip + 1 }}–{{ auditPage.skip + auditPage.entries.length }}
          of {{ auditPage.total }}
        </span>
        <button class="link-btn"
                :disabled="auditLoading || (auditPage.skip + auditPage.take) >= auditPage.total"
                @click="loadAudit(auditPage.skip + auditPage.take)">
          Older
        </button>
      </div>
    </template>

    <!-- Decision confirm -->
    <div v-if="decisionRow" class="modal-backdrop" @click.self="closeDecision">
      <div class="modal-card">
        <div class="modal-header">
          <div>
            <h3 class="modal-title">
              {{ decisionVerdict === 'approve' ? 'Approve concession' : 'Reject concession' }}
            </h3>
            <div class="modal-subtitle">
              {{ borrowerName(decisionRow) }}
              <span v-if="decisionRow.loan_number"> — {{ decisionRow.loan_number }}</span>
            </div>
          </div>
          <button class="close-btn" @click="closeDecision">✕</button>
        </div>

        <div class="modal-body">
          <div class="decision-summary">
            <div>{{ formatNumber(decisionRow.requested_bps) }} bps
              <span v-if="decisionRow.requested_amount">({{ formatMoney(decisionRow.requested_amount) }})</span>
              &middot; {{ decisionRow.tier === 'corp' ? 'Corporate' : 'Branch' }}
              &middot; {{ productLabel(decisionRow.product_type) }}
            </div>
            <div class="sub-note">{{ decisionRow.reason_text || decisionRow.reason_code }}</div>
            <div v-if="decisionRow.requester_notes" class="sub-note">{{ decisionRow.requester_notes }}</div>
          </div>

          <div v-if="decisionVerdict === 'approve'" class="decision-note-banner">
            Approving creates the price exception in Polly on the loan.
          </div>

          <label class="form-field full">
            <span class="form-label">Notes</span>
            <textarea v-model.trim="decisionNotes" class="form-input form-textarea" rows="3"
                      placeholder="Optional — recorded on the audit trail"></textarea>
          </label>

          <div v-if="decisionError" class="inline-error">{{ decisionError }}</div>

          <div class="modal-footer-actions">
            <button class="cancel-btn" :disabled="decidingId" @click="closeDecision">Cancel</button>
            <button :class="decisionVerdict === 'approve' ? 'save-btn' : 'reject-confirm-btn'"
                    :disabled="decidingId" @click="confirmDecision">
              {{ decidingId ? 'Working...' : (decisionVerdict === 'approve' ? 'Approve' : 'Reject') }}
            </button>
          </div>
        </div>
      </div>
    </div>
      <div v-if="showApproverModal" class="modal-backdrop" @click.self="closeApproverImport">
        <div class="modal-card approver-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Concession Approvers</h3>
              <div class="modal-subtitle">
                Upload the "Polly User Access Level" workbook to refresh the roster
              </div>
            </div>
            <button class="close-btn" @click="closeApproverImport">✕</button>
          </div>

          <div class="modal-body approver-modal-body">
            <!-- current roster summary -->
            <div class="approver-current">
              <span v-if="approverRosterLoading">Loading the current roster...</span>
              <template v-else>
                <strong>{{ approverRosterCount }}</strong> active
                {{ approverRosterCount === 1 ? 'user' : 'users' }} on the roster
                <span v-if="approverLastImport" class="approver-current-sub">
                  — last import {{ formatDateTime(approverLastImport.committed_utc || approverLastImport.created_utc, true) }}
                  by {{ approverLastImport.uploaded_by_name || approverLastImport.uploaded_by_email || 'unknown' }}
                </span>
                <span v-else class="approver-current-sub">— never imported</span>
              </template>
            </div>

            <!-- Reason list. Polly owns this one, so it is a refresh, not an
                 edit surface. Lives here because this is the admin surface. -->
            <div class="approver-current">
              <span v-if="reasonsLoading">Loading the reason list...</span>
              <template v-else>
                <strong>{{ reasonCount }}</strong> active concession
                {{ reasonCount === 1 ? 'reason' : 'reasons' }}
                <span v-if="reasonLastSynced" class="approver-current-sub">
                  — last synced {{ formatDateTime(reasonLastSynced, true) }}
                </span>
                <span v-else class="approver-current-sub">— not synced yet</span>
                <button class="approver-toggle"
                        :disabled="reasonSyncing"
                        @click="syncConcessionReasons">
                  {{ reasonSyncing ? 'Refreshing...' : 'Refresh now' }}
                </button>
              </template>

              <div class="approver-table-note">
                Polly owns this list — it syncs on its own in the background. Refresh only if you
                changed something in Polly and want it reflected immediately.
              </div>

              <div v-if="reasonSyncMessage" class="approver-table-note">{{ reasonSyncMessage }}</div>

              <!-- A Polly type we could not map to branch/corp means that reason
                   is offered for BOTH tiers. Worth saying out loud. -->
              <div v-if="reasonUnmappedTypes.length" class="approver-table-note">
                Unrecognized Polly types: <strong>{{ reasonUnmappedTypes.join(', ') }}</strong>.
                Reasons with those types are offered for both branch and corp requests.
              </div>

              <div v-if="reasonSyncError" class="inline-error">{{ reasonSyncError }}</div>
            </div>

            <!-- file picker -->
            <div class="approver-drop"
                 :class="{ 'is-over': approverDragOver, 'has-file': !!approverFile }"
                 @dragover.prevent="approverDragOver = true"
                 @dragleave.prevent="approverDragOver = false"
                 @drop.prevent="onApproverDrop">
              <input ref="approverFileInput"
                     type="file"
                     accept=".xlsx,.xlsm"
                     class="approver-file-input"
                     @change="onApproverFilePicked" />

              <template v-if="approverFile">
                <i class="fa-duotone fa-file-excel approver-drop-icon"></i>
                <div class="approver-drop-title">{{ approverFile.name }}</div>
                <div class="approver-drop-hint">{{ formatFileSize(approverFile.size) }}</div>
              </template>
              <template v-else>
                <i class="fa-duotone fa-cloud-arrow-up approver-drop-icon"></i>
                <div class="approver-drop-title">Drop the .xlsx here</div>
                <div class="approver-drop-hint">or click to choose a file</div>
              </template>

              <button class="approver-browse-btn"
                      :disabled="approverBusy"
                      @click="$refs.approverFileInput.click()">
                {{ approverFile ? 'Choose a different file' : 'Choose file' }}
              </button>
            </div>

            <div v-if="approverError" class="inline-error">{{ approverError }}</div>

            <div v-if="approverBusy" class="approver-busy">
              <i class="fa-duotone fa-spinner fa-spin"></i>
              {{ approverCommitting ? 'Applying the import...' : 'Reading the workbook...' }}
            </div>

            <!-- preview -->
            <template v-if="approverPreview && !approverBusy">
              <div class="approver-result-banner"
                   :class="approverPreview.committed ? 'is-committed' : 'is-preview'">
                <i :class="approverPreview.committed ? 'fa-duotone fa-circle-check' : 'fa-duotone fa-eye'"></i>
                <span v-if="approverPreview.committed">
                  Import applied. The roster is now live.
                </span>
                <span v-else>
                  Preview only — nothing has been saved yet.
                </span>
              </div>

              <div class="approver-counts">
                <div class="approver-count-card">
                  <div class="approver-count-value">{{ approverPreview.rows_read }}</div>
                  <div class="approver-count-label">Rows read</div>
                </div>
                <div class="approver-count-card is-new">
                  <div class="approver-count-value">{{ approverPreview.rows_new }}</div>
                  <div class="approver-count-label">New</div>
                </div>
                <div class="approver-count-card is-changed">
                  <div class="approver-count-value">{{ approverPreview.rows_changed }}</div>
                  <div class="approver-count-label">Updated</div>
                </div>
                <div class="approver-count-card is-off">
                  <div class="approver-count-value">{{ approverPreview.rows_deactivating }}</div>
                  <div class="approver-count-label">Deactivated</div>
                </div>
                <div class="approver-count-card">
                  <div class="approver-count-value">{{ approverPreview.rows_unchanged }}</div>
                  <div class="approver-count-label">Unchanged</div>
                </div>
                <div class="approver-count-card" v-if="approverPreview.rows_skipped">
                  <div class="approver-count-value">{{ approverPreview.rows_skipped }}</div>
                  <div class="approver-count-label">Skipped</div>
                </div>
              </div>

              <!-- deactivation is the destructive part; call it out -->
              <div v-if="approverPreview.rows_deactivating > 0 && !approverPreview.committed"
                   class="approver-warn-banner">
                <i class="fa-duotone fa-triangle-exclamation"></i>
                <span>
                  {{ approverPreview.rows_deactivating }}
                  {{ approverPreview.rows_deactivating === 1 ? 'user is' : 'users are' }}
                  missing from this file and will be switched off. They are never deleted — a later
                  upload that includes them turns them back on.
                </span>
              </div>

              <!-- per-division rollup -->
              <div class="approver-section-title">By division</div>
              <div class="approver-table-wrap">
                <table class="approver-table">
                  <thead>
                    <tr>
                      <th>Division</th>
                      <th class="num">Users</th>
                      <th class="num">Branch approvers</th>
                      <th class="num">Corp approvers</th>
                      <th class="num">Ceilings set</th>
                      <th>Can request</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="d in approverPreview.divisions" :key="d.division">
                      <td>{{ d.division }}</td>
                      <td class="num">{{ d.user_count }}</td>
                      <td class="num" :class="{ 'is-zero': d.can_request && d.branch_approver_count === 0 }">
                        {{ d.branch_approver_count }}
                      </td>
                      <td class="num" :class="{ 'is-zero': d.can_request && d.corp_approver_count === 0 }">
                        {{ d.corp_approver_count }}
                      </td>
                      <td class="num" :class="{ 'is-zero': d.can_request && d.threshold_cells_populated === 0 }">
                        {{ d.threshold_cells_populated }}
                      </td>
                      <td>
                        <span class="approver-pill" :class="d.can_request ? 'is-yes' : 'is-no'">
                          {{ d.can_request ? 'Yes' : 'Restricted' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="approver-table-note">
                A division with users but no approvers has nobody to send an over-threshold request to,
                so those land in this queue as concession requests. Zero ceilings set means every
                concession there auto-approves.
              </div>

              <!-- warnings -->
              <div v-if="approverPreview.warnings_total > 0">
                <div class="approver-section-title">
                  Warnings ({{ approverPreview.warnings_total }})
                  <button class="approver-toggle" @click="showApproverWarnings = !showApproverWarnings">
                    {{ showApproverWarnings ? 'Hide' : 'Show' }}
                  </button>
                </div>
                <div v-if="showApproverWarnings" class="approver-warn-list">
                  <div v-for="(w, i) in approverPreview.warnings" :key="i" class="approver-warn-row">
                    <span class="approver-warn-row-loc">Row {{ w.row }}</span>
                    <span class="approver-warn-row-who">{{ w.email || w.field }}</span>
                    <span class="approver-warn-row-msg">{{ w.message }}</span>
                  </div>
                  <div v-if="approverPreview.warnings_truncated" class="approver-warn-more">
                    Showing the first {{ approverPreview.warnings.length }} of
                    {{ approverPreview.warnings_total }} warnings.
                  </div>
                </div>
                <div class="approver-table-note">
                  Warnings never block an import — they flag cells worth a second look in the workbook.
                </div>
              </div>

              <!-- change detail -->
              <div v-if="approverPreview.changes && approverPreview.changes.length">
                <div class="approver-section-title">
                  What changes
                  <button class="approver-toggle" @click="showApproverChanges = !showApproverChanges">
                    {{ showApproverChanges ? 'Hide' : 'Show' }}
                  </button>
                </div>
                <div v-if="showApproverChanges" class="approver-change-list">
                  <div v-for="(c, i) in approverPreview.changes" :key="i" class="approver-change-row">
                    <span class="approver-change-tag" :class="'tag-' + c.change_type">
                      {{ changeTypeLabel(c.change_type) }}
                    </span>
                    <span class="approver-change-who">
                      {{ c.name || c.email }}
                      <span class="approver-change-email" v-if="c.name">{{ c.email }}</span>
                    </span>
                    <span class="approver-change-div">{{ c.division || '—' }}</span>
                    <span class="approver-change-fields">
                      <span v-for="(f, fi) in c.fields" :key="fi" class="approver-change-field">
                        {{ f.field }}: <em>{{ f.from || 'empty' }}</em> → <strong>{{ f.to || 'empty' }}</strong>
                      </span>
                    </span>
                  </div>
                  <div v-if="approverPreview.changes_truncated" class="approver-warn-more">
                    Showing the first {{ approverPreview.changes.length }} rows. The counts above are complete.
                  </div>
                </div>
              </div>
            </template>

            <div class="modal-footer-actions">
              <button class="cancel-btn" :disabled="approverBusy" @click="closeApproverImport">
                {{ approverPreview && approverPreview.committed ? 'Done' : 'Cancel' }}
              </button>
              <button v-if="approverPreview && !approverPreview.committed"
                      class="save-btn"
                      :disabled="approverBusy || !approverFile"
                      @click="commitApproverImport">
                Apply import
              </button>
              <button v-else-if="!approverPreview"
                      class="save-btn"
                      :disabled="approverBusy || !approverFile"
                      @click="previewApproverImport">
                Preview changes
              </button>
            </div>
          </div>
        </div>
      </div>

  </div>
</template>

<script>
  import api from '@/api/api.service'
  import auth from '@/authConfig.js'

  export default {
    name: 'Concessions',
    data() {
      return {
        tab: 'request',
        contextLoading: true,
        loading: false,
        days: 90,
        statusFilter: '',
        divisionFilter: '',

        context: {
          email: '',
          found: false,
          can_request: false,
          blocked_reason: '',
          roster_note: '',
          division: '',
          branch_code: '',
          branch_name: '',
          is_approver: false,
          approver_divisions: [],
          is_concessions_admin: false,
          all_divisions: [],
          branch_reasons: [],
          corp_reasons: [],
          products: []
        },

        // Polly intake diagnostics (admin tab).
        pollyEvents: [],
        pollyDays: 14,
        pollyLoading: false,
        pollyError: '',
        // Filtered client-side: the day window already bounds the result set, so
        // sifting is instant and does not re-hit the API on every change.
        pollyFilters: {
          mode: '',
          event: '',
          action: '',
          division: '',
          tier: '',
          raisedBy: '',
          pollyUser: '',
          loan: '',
          flag: ''
        },

        // Concession approver roster importer (managers only). The roster is
        // maintained in the "Polly User Access Level" workbook, not in this app.
        showApproverModal: false,
        approverFile: null,
        approverDragOver: false,
        approverPreview: null,   // the server's diff report, preview or applied
        approverError: '',
        approverBusy: false,
        approverCommitting: false,
        approverRosterCount: 0,
        approverRosterLoading: false,
        approverLastImport: null,
        showApproverWarnings: false,
        showApproverChanges: false,
        // Concession reason list — mirrored from Polly's price-exception reasons.
        reasonCount: 0,
        reasonLastSynced: null,
        reasonsLoading: false,
        reasonSyncing: false,
        reasonSyncMessage: '',
        reasonSyncError: '',
        reasonUnmappedTypes: [],

        form: this.emptyForm(),
        submitting: false,
        previewing: false,
        isPreview: false,
        submitError: '',
        submitResult: null,

        requests: [],
        expandedId: null,
        events: [],
        eventsLoading: false,

        // Audit feed. Filters live in `audit`; the server returns the page plus
        // the distinct actions/actors present, so the dropdowns show what is
        // actually there rather than a hardcoded list.
        audit: {
          days: 90,
          event_type: '',
          actor_email: '',
          division: '',
          loan_number: ''
        },
        auditPage: { entries: [], total: 0, skip: 0, take: 100, event_types: [], actors: [] },
        auditLoading: false,

        decisionRow: null,
        decisionVerdict: '',
        decisionNotes: '',
        decisionError: '',
        decidingId: null
      }
    },
    computed: {
      reasonsForTier() {
        return this.form.tier === 'corp' ? this.context.corp_reasons : this.context.branch_reasons
      },
      selectedReasonRequiresNote() {
        const r = this.reasonsForTier.find(x => x.code === this.form.reason_code)
        return !!(r && r.requires_note)
      },
      canSubmit() {
        if (!this.form.loan_id || !this.form.product_type || !this.form.tier) return false
        if (!this.form.reason_code) return false
        if (this.selectedReasonRequiresNote && !this.form.requester_notes) return false
        const bps = Number(this.form.requested_bps) || 0
        const amt = Number(this.form.requested_amount) || 0
        return bps > 0 || amt > 0
      },
      // Shows the other side of the bps/dollar pair so the LO can sanity-check
      // the number before submitting. Same math the server uses.
      derivedAmountLabel() {
        const loan = Number(this.form.loan_amount) || 0
        if (loan <= 0) return ''

        const bps = Number(this.form.requested_bps) || 0
        const amt = Number(this.form.requested_amount) || 0

        if (bps > 0) return `≈ ${this.formatMoney((bps / 10000) * loan)}`
        if (amt > 0) return `≈ ${this.formatNumber((amt / loan) * 10000)} bps`
        return ''
      },
      // Only meaningful on a preview, where Polly returns both sides.
      priceDelta() {
        const before = this.submitResult?.polly?.prior_final_price
        const after = this.submitResult?.polly?.exception_final_price
        if (before === null || before === undefined) return null
        if (after === null || after === undefined) return null
        return Number(after) - Number(before)
      },
      pollyDryRunCount() {
        return this.pollyEvents.filter(x => x.dry_run).length
      },
      // Options come from the rows actually loaded, so a filter can never offer
      // a value that would return nothing.
      pollyFacets() {
        const distinct = (key) => [...new Set(
          this.pollyEvents.map(r => r[key]).filter(v => v !== null && v !== undefined && v !== '')
        )].sort()

        return {
          events: distinct('polly_event'),
          actions: distinct('action'),
          divisions: distinct('division'),
          tiers: distinct('tier'),
          raisedBy: distinct('requested_by_email'),
          pollyUsers: distinct('polly_user_email')
        }
      },
      filteredPollyEvents() {
        const f = this.pollyFilters
        const loan = (f.loan || '').trim().toLowerCase()

        return this.pollyEvents.filter(r => {
          if (f.mode === 'dry' && !r.dry_run) return false
          if (f.mode === 'live' && r.dry_run) return false
          if (f.event && r.polly_event !== f.event) return false
          if (f.action && r.action !== f.action) return false
          if (f.division && r.division !== f.division) return false
          if (f.tier && r.tier !== f.tier) return false
          if (f.raisedBy && r.requested_by_email !== f.raisedBy) return false
          if (f.pollyUser && r.polly_user_email !== f.pollyUser) return false

          if (loan && !String(r.loan_number || '').toLowerCase().includes(loan)) return false

          // The flags are the reason this tab exists — each one is a distinct
          // way the pipeline declined to do the obvious thing.
          switch (f.flag) {
            case 'no_owner':
              return !r.owner_resolved
            case 'reason_unmatched':
              return !!r.reason_code && !r.reason_matched
            case 'bypassed':
              return !!r.internal_approval_bypassed
            case 'emailed':
              return !!r.emails_sent
            case 'clean':
              return r.owner_resolved &&
                     !(r.reason_code && !r.reason_matched) &&
                     !r.internal_approval_bypassed
            default:
              return true
          }
        })
      },
      pollyFiltersActive() {
        const f = this.pollyFilters
        return !!(f.mode || f.event || f.action || f.division || f.tier ||
                  f.raisedBy || f.pollyUser || f.loan || f.flag)
      },
      // An admin filters across every division; everyone else only sees the
      // ones they approve for. Server-side scoping is what actually enforces
      // this — the list here just stops the UI offering a filter that would
      // come back empty.
      filterDivisions() {
        return this.context.is_concessions_admin
          ? (this.context.all_divisions || [])
          : (this.context.approver_divisions || [])
      },
      pendingForMe() {
        return this.requests.filter(r => r.status === 'pending')
      },
      // Approved here but Polly hasn't taken it. Called out because the price
      // exception does not exist on the loan until Polly accepts.
      pollyProblems() {
        return this.requests.filter(r =>
          (r.polly_sync_status === 'failed' || r.polly_sync_status === 'pending') &&
          (r.status === 'approved' || r.status === 'auto_approved'))
      },
      avgBps() {
        const vals = this.requests
          .map(r => Number(r.requested_bps))
          .filter(v => !Number.isNaN(v) && v > 0)
        if (!vals.length) return '—'
        return this.formatNumber(vals.reduce((a, b) => a + b, 0) / vals.length)
      },
      totalDollars() {
        const total = this.requests
          .filter(r => r.status === 'approved' || r.status === 'auto_approved')
          .reduce((sum, r) => sum + (Number(r.requested_amount) || 0), 0)
        return total > 0 ? this.formatMoney(total) : '—'
      }
    },
    async mounted() {
      await this.loadContext()
      // Approvers land on the queue they're expected to act on.
      if (this.context.is_approver) {
        this.tab = 'dashboard'
        await this.loadRequests()
      } else if (!this.context.can_request) {
        this.tab = 'dashboard'
        await this.loadRequests()
      }
    },
    methods: {
      emptyForm() {
        return {
          loan_id: '',
          loan_number: '',
          borrower_first: '',
          borrower_last: '',
          loan_amount: null,
          product_type: 'conv',
          tier: 'branch',
          requested_bps: null,
          requested_amount: null,
          reason_code: '',
          requester_notes: ''
        }
      },
      resetForm() {
        this.form = this.emptyForm()
        this.submitResult = null
        this.submitError = ''
        this.isPreview = false
      },
      onTierChange() {
        // Reason lists differ per tier, so a stale selection would submit a
        // reason Polly doesn't offer for this tier.
        if (!this.reasonsForTier.some(r => r.code === this.form.reason_code)) {
          this.form.reason_code = ''
        }
      },
      async reload() {
        await this.loadContext()
        if (this.tab === 'dashboard') await this.loadRequests()
      },
      async switchToAudit() {
        this.tab = 'audit'
        if (!this.auditPage.entries.length) await this.loadAudit(0)
      },
      // ── Concession approvers importer ────────────────────────────────────
      // The roster lives in the "Polly User Access Level" workbook. A manager
      // uploads it here; the server parses, diffs, and only writes on commit.
      openApproverImport() {
        this.showApproverModal = true
        this.resetApproverImport()
        this.loadApproverRosterSummary()
        this.loadConcessionReasonsSummary()
      },
      closeApproverImport() {
        if (this.approverBusy) return
        this.showApproverModal = false
        this.resetApproverImport()
      },
      resetApproverImport() {
        this.approverFile = null
        this.approverPreview = null
        this.approverError = ''
        this.approverDragOver = false
        this.approverCommitting = false
        this.showApproverWarnings = false
        this.showApproverChanges = false
        if (this.$refs.approverFileInput) this.$refs.approverFileInput.value = ''
      },
      async loadApproverRosterSummary() {
        this.approverRosterLoading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken
          const [rosterRes, importRes] = await Promise.allSettled([
            api.GetConcessionApprovers(true, token),
            api.GetConcessionApproverImports(1, token)
          ])

          if (rosterRes.status === 'fulfilled') {
            const data = rosterRes.value?.data?.data || rosterRes.value?.data || []
            this.approverRosterCount = Array.isArray(data) ? data.length : 0
          }

          if (importRes.status === 'fulfilled') {
            const data = importRes.value?.data?.data || importRes.value?.data || []
            this.approverLastImport = Array.isArray(data) && data.length ? data[0] : null
          }
        } catch {
          // A failed summary is cosmetic — the upload itself still works.
        } finally {
          this.approverRosterLoading = false
        }
      },
      onApproverFilePicked(event) {
        const file = event?.target?.files?.[0]
        if (file) this.setApproverFile(file)
      },
      onApproverDrop(event) {
        this.approverDragOver = false
        const file = event?.dataTransfer?.files?.[0]
        if (file) this.setApproverFile(file)
      },
      setApproverFile(file) {
        const name = (file.name || '').toLowerCase()
        if (!name.endsWith('.xlsx') && !name.endsWith('.xlsm')) {
          this.approverError = 'That is not an Excel workbook. Pick the .xlsx export from Polly.'
          return
        }

        this.approverFile = file
        // A new file invalidates any preview on screen — otherwise a manager
        // could confirm one file's numbers and apply a different file.
        this.approverPreview = null
        this.approverError = ''
        this.showApproverWarnings = false
        this.showApproverChanges = false
      },
      previewApproverImport() {
        return this._runApproverImport(false)
      },
      commitApproverImport() {
        return this._runApproverImport(true)
      },
      async _runApproverImport(commit) {
        if (!this.approverFile || this.approverBusy) return

        this.approverBusy = true
        this.approverCommitting = commit
        this.approverError = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken
          const res = await api.ImportConcessionApprovers(this.approverFile, commit, token)

          const body = res?.data || {}
          const report = body.data || body

          // The API lowercases every PascalCase property (LowerCaseNamingPolicy
          // in Startup.cs), so the ServiceResponse flag arrives as `issuccess`.
          // Our own DTO fields carry [JsonPropertyName] and keep snake_case.
          const succeeded = body.issuccess !== undefined ? body.issuccess : body.isSuccess

          if (succeeded === false) {
            this.approverError = body.message || 'The import failed.'
            // Keep whatever the server managed to report, so counts and
            // warnings are still visible alongside the error.
            if (report && typeof report === 'object') this.approverPreview = report
            return
          }

          this.approverPreview = report

          if (commit) {
            // Refresh the summary line so the roster count reflects the import.
            this.loadApproverRosterSummary()
          } else if (report?.warnings_total > 0) {
            this.showApproverWarnings = true
          }
        } catch (e) {
          // A parse failure or a refused empty file comes back as a 400, so the
          // useful text is on the error response, not the success path.
          const serverMessage = e?.response?.data?.message
            || e?.response?.data?.error
          this.approverError = serverMessage
            || 'Could not reach the server to run the import. Nothing was changed.'
        } finally {
          this.approverBusy = false
          this.approverCommitting = false
        }
      },
      async loadConcessionReasonsSummary() {
        this.reasonsLoading = true
        this.reasonSyncError = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionReasons(null, tokenResponse.accessToken)
          const data = res?.data?.data || []
          this.applyReasonSummary(Array.isArray(data) ? data : [])
        } catch {
          // Cosmetic — the sync button still works if this read failed.
        } finally {
          this.reasonsLoading = false
        }
      },
      applyReasonSummary(reasons) {
        this.reasonCount = reasons.length
        // Newest sync stamp across the list. Nulls are omitted by the API, so
        // filter before comparing.
        const stamps = reasons
          .map(r => r.polly_synced_utc)
          .filter(Boolean)
          .sort()
        this.reasonLastSynced = stamps.length ? stamps[stamps.length - 1] : null
      },
      async syncConcessionReasons() {
        if (this.reasonSyncing) return

        this.reasonSyncing = true
        this.reasonSyncError = ''
        this.reasonSyncMessage = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.SyncConcessionReasonsFromPolly(tokenResponse.accessToken)
          const body = res?.data || {}
          const result = body.data || {}

          this.reasonSyncMessage = body.message || 'Reasons synced.'
          this.reasonUnmappedTypes = Array.isArray(result.unmapped_types) ? result.unmapped_types : []
          this.applyReasonSummary(Array.isArray(result.reasons) ? result.reasons : [])
        } catch (e) {
          this.reasonSyncError = e?.response?.data?.message
            || 'Could not reach Polly to refresh the reason list. The existing reasons are unchanged.'
        } finally {
          this.reasonSyncing = false
        }
      },
      changeTypeLabel(changeType) {
        switch (changeType) {
          case 'new': return 'New'
          case 'changed': return 'Updated'
          case 'deactivating': return 'Deactivating'
          case 'reactivating': return 'Reactivating'
          default: return changeType || ''
        }
      },
      formatFileSize(bytes) {
        const size = Number(bytes)
        if (!size || Number.isNaN(size)) return ''
        if (size < 1024) return `${size} B`
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`
        return `${(size / (1024 * 1024)).toFixed(1)} MB`
      },
      async switchToDiagnostics() {
        this.tab = 'diagnostics'
        if (!this.pollyEvents.length) await this.loadPollyEvents()
      },
      clearPollyFilters() {
        this.pollyFilters = {
          mode: '', event: '', action: '', division: '',
          tier: '', raisedBy: '', pollyUser: '', loan: '', flag: ''
        }
      },
      async loadPollyEvents() {
        this.pollyLoading = true
        this.pollyError = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionPollyEventLog(this.pollyDays, tokenResponse.accessToken)
          const body = res?.data || {}

          // The endpoint refuses non-admins server-side; surface that rather than
          // showing an empty table as though nothing had happened.
          if (body.issuccess === false || body.isSuccess === false) {
            this.pollyEvents = []
            this.pollyError = body.message || 'Could not load the Polly intake diagnostics.'
            return
          }

          this.pollyEvents = body.data || []
        } catch (e) {
          this.pollyEvents = []
          this.pollyError =
            e?.response?.data?.message ||
            e?.message ||
            'Could not load the Polly intake diagnostics.'
        } finally {
          this.pollyLoading = false
        }
      },
      async loadAudit(skip) {
        this.auditLoading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionAuditTrail({
            days: this.audit.days,
            event_type: this.audit.event_type || null,
            actor_email: this.audit.actor_email || null,
            division: this.audit.division || null,
            loan_number: this.audit.loan_number || null,
            skip: skip || 0,
            take: this.auditPage.take || 100
          }, tokenResponse.accessToken)

          const page = res?.data?.data
          if (page) {
            this.auditPage = {
              entries: page.entries || [],
              total: page.total || 0,
              skip: page.skip || 0,
              take: page.take || 100,
              // Keep the previous facets when a filter narrows things to
              // nothing, otherwise the dropdowns empty out and the user is
              // stuck unable to widen the filter again.
              event_types: (page.event_types && page.event_types.length)
                ? page.event_types
                : this.auditPage.event_types,
              actors: (page.actors && page.actors.length)
                ? page.actors
                : this.auditPage.actors
            }
          }
        } catch {
          this.auditPage = { ...this.auditPage, entries: [], total: 0 }
        } finally {
          this.auditLoading = false
        }
      },
      auditEventClass(type) {
        if (type === 'approved' || type === 'auto_approved' ||
            type === 'polly_price_exception_created') return 'is-approved'
        if (type === 'rejected' || type === 'polly_push_failed') return 'is-rejected'
        if (type === 'revenue_alert_created') return 'is-warn'
        if (type === 'created' || type === 'routed') return 'is-pending'
        return 'is-neutral'
      },
      async switchToDashboard() {
        this.tab = 'dashboard'
        if (!this.requests.length) await this.loadRequests()
      },
      async loadContext() {
        this.contextLoading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionUserContext(tokenResponse.accessToken)
          const data = res?.data?.data
          if (data) {
            this.context = {
              ...this.context,
              ...data,
              approver_divisions: data.approver_divisions || [],
              all_divisions: data.all_divisions || [],
              branch_reasons: data.branch_reasons || [],
              corp_reasons: data.corp_reasons || [],
              products: data.products || []
            }
          }
        } catch (e) {
          this.submitError = this.serverMessage(e, 'Could not load your concession access.')
        } finally {
          this.contextLoading = false
        }
      },
      async loadRequests() {
        this.loading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionRequests(
            this.days, this.statusFilter, this.divisionFilter, tokenResponse.accessToken)
          const data = res?.data?.data || []
          this.requests = Array.isArray(data) ? data : []
        } catch (e) {
          this.requests = []
        } finally {
          this.loading = false
        }
      },
      // One payload builder so a preview cannot check something different from
      // what a submit then files.
      buildPayload() {
        return {
          loan_id: this.form.loan_id || null,
          loan_number: this.form.loan_number || null,
          borrower_first: this.form.borrower_first || null,
          borrower_last: this.form.borrower_last || null,
          loan_amount: this.form.loan_amount || null,
          tier: this.form.tier,
          product_type: this.form.product_type,
          requested_bps: this.form.requested_bps || null,
          requested_amount: this.form.requested_amount || null,
          reason_code: this.form.reason_code,
          requester_notes: this.form.requester_notes || null
        }
      },
      async preview() {
        if (!this.canSubmit || this.submitting) return

        this.submitting = true
        this.previewing = true
        this.submitError = ''
        this.submitResult = null

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.PreviewConcessionRequest(this.buildPayload(), tokenResponse.accessToken)
          this.submitResult = res?.data?.data || null
          this.isPreview = true
          // The form is deliberately left untouched — a preview exists so the
          // same values can then be submitted.
        } catch (e) {
          this.submitError = this.serverMessage(e, 'Could not preview the concession.')
        } finally {
          this.submitting = false
          this.previewing = false
        }
      },
      async submit() {
        if (!this.canSubmit || this.submitting) return

        this.submitting = true
        this.submitError = ''
        this.submitResult = null
        this.isPreview = false

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.SubmitConcessionRequest(this.buildPayload(), tokenResponse.accessToken)
          this.submitResult = res?.data?.data || null

          // Keep the outcome on screen but clear the loan so the next request
          // starts clean rather than resubmitting the same one.
          this.form.loan_id = ''
          this.form.loan_number = ''
          this.form.requested_bps = null
          this.form.requested_amount = null
          this.form.requester_notes = ''

          this.requests = []
        } catch (e) {
          this.submitError = this.serverMessage(e, 'Could not submit the concession. Nothing was recorded.')
        } finally {
          this.submitting = false
        }
      },
      openDecision(row, verdict) {
        this.decisionRow = row
        this.decisionVerdict = verdict
        this.decisionNotes = ''
        this.decisionError = ''
      },
      closeDecision() {
        if (this.decidingId) return
        this.decisionRow = null
        this.decisionVerdict = ''
        this.decisionNotes = ''
        this.decisionError = ''
      },
      async confirmDecision() {
        if (!this.decisionRow || this.decidingId) return

        this.decidingId = this.decisionRow.id
        this.decisionError = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.DecideConcessionRequest({
            id: this.decisionRow.id,
            decision: this.decisionVerdict,
            decision_notes: this.decisionNotes || null
          }, tokenResponse.accessToken)

          const result = res?.data?.data
          // Polly may have refused even though the decision landed. Surface it
          // rather than closing on a green note.
          if (result?.polly_error) {
            this.decisionError = `Decision recorded, but Polly hasn't accepted it: ${result.polly_error}`
            await this.loadRequests()
            return
          }

          this.decisionRow = null
          this.decisionVerdict = ''
          await this.loadRequests()
        } catch (e) {
          this.decisionError = this.serverMessage(e, 'Could not record the decision.')
        } finally {
          this.decidingId = null
        }
      },
      async toggleDetail(row) {
        if (this.expandedId === row.id) {
          this.expandedId = null
          return
        }

        this.expandedId = row.id
        this.events = []
        this.eventsLoading = true

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionRequestEvents(row.id, tokenResponse.accessToken)
          const data = res?.data?.data || []
          this.events = Array.isArray(data) ? data : []
        } catch {
          this.events = []
        } finally {
          this.eventsLoading = false
        }
      },

      // ── display helpers ──────────────────────────────────────────────────
      // The API lowercases PascalCase properties, so ServiceResponse.Message
      // arrives as `message`, and failures come back as a 400.
      serverMessage(error, fallback) {
        return error?.response?.data?.message || fallback
      },
      countBy(status) {
        return this.requests.filter(r => r.status === status).length
      },
      borrowerName(r) {
        const name = [r?.borrower_first, r?.borrower_last].filter(Boolean).join(' ')
        return name || '—'
      },
      productLabel(code) {
        switch (code) {
          case 'conv': return 'Conventional'
          case 'fha': return 'FHA'
          case 'usda': return 'USDA'
          case 'va': return 'VA'
          case 'jumbo': return 'Jumbo'
          case 'nonqm': return 'Non QM'
          default: return code || '—'
        }
      },
      statusLabel(status) {
        switch (status) {
          case 'pending': return 'Pending'
          case 'auto_approved': return 'Auto-approved'
          case 'approved': return 'Approved'
          case 'rejected': return 'Rejected'
          case 'cancelled': return 'Cancelled'
          case 'expired': return 'Expired'
          default: return status || '—'
        }
      },
      statusClass(status) {
        if (status === 'approved' || status === 'auto_approved') return 'is-approved'
        if (status === 'rejected') return 'is-rejected'
        if (status === 'pending') return 'is-pending'
        return 'is-neutral'
      },
      pollyLabel(state) {
        switch (state) {
          case 'sent': return 'In Polly'
          case 'pending': return 'Not sent'
          case 'failed': return 'Failed'
          case 'not_required': return 'N/A'
          default: return '—'
        }
      },
      pollyClass(state) {
        if (state === 'sent') return 'is-approved'
        if (state === 'failed') return 'is-rejected'
        if (state === 'pending') return 'is-pending'
        return 'is-neutral'
      },
      routingLabel(routing) {
        switch (routing) {
          case 'auto': return 'Auto-approved, nobody notified'
          case 'approvers': return 'Sent to the configured approvers'
          case 'lock_desk': return 'Lock desk (no approvers configured for the division)'
          default: return routing || '—'
        }
      },
      eventLabel(type) {
        switch (type) {
          case 'created': return 'Filed'
          case 'auto_approved': return 'Auto-approved'
          case 'routed': return 'Routed'
          case 'approved': return 'Approved'
          case 'rejected': return 'Rejected'
          case 'polly_price_exception_created': return 'Created in Polly'
          case 'polly_push_failed': return 'Polly push failed'
          case 'branch_manager_notified': return 'Branch manager alerted'
          case 'revenue_alert_created': return 'Revenue alert raised'
          case 'revenue_alert_checked': return 'Revenue check run'
          default: return (type || '').replace(/_/g, ' ')
        }
      },
      formatDateTime(value, withTime = false) {
        if (!value) return '—'
        const str = typeof value === 'string' && !value.endsWith('Z') && !value.includes('+')
          ? value + 'Z'
          : value
        const date = new Date(str)
        if (Number.isNaN(date.getTime())) return '—'
        if (withTime) {
          return date.toLocaleDateString('en-US') + ' ' +
            date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        }
        return date.toLocaleDateString('en-US')
      },
      formatNumber(value) {
        const n = Number(value)
        if (value === null || value === undefined || Number.isNaN(n)) return '—'
        return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
      },
      // Polly prices are points, shown to 3 decimals as they are elsewhere.
      formatPrice(value) {
        const n = Number(value)
        if (value === null || value === undefined || Number.isNaN(n)) return '—'
        return n.toFixed(3)
      },
      formatMoney(value) {
        const n = Number(value)
        if (value === null || value === undefined || Number.isNaN(n)) return '—'
        return n.toLocaleString('en-US', {
          style: 'currency', currency: 'USD', maximumFractionDigits: 0
        })
      }
    }
  }
</script>

<style scoped>
  .concessions-page {
    display: flex;
    flex-direction: column;
    padding: 20px 24px 32px;
    background: #f7f9fc;
    min-height: 100%;
    font-family: 'Segoe UI', Tahoma, sans-serif;
    color: #22293a;
  }

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }
  .page-header-left { display: flex; align-items: center; gap: 12px; }
  .page-icon {
    height: 44px;
    width: 44px;
    border-radius: 8px;
    background: #123a71;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 19px;
  }
  .page-title { font-size: 21px; font-weight: 600; margin: 0; }
  .page-subtitle { font-size: 12.5px; color: #7b8397; margin: 2px 0 0; }
  .header-actions { display: flex; align-items: center; gap: 8px; }

  .filter-select, .refresh-btn {
    height: 38px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #3c4658;
    font-size: 13px;
    padding: 0 10px;
    cursor: pointer;
  }
  .refresh-btn:hover:not(:disabled) { background: #eef4ff; border-color: #c2d4f0; }
  .refresh-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .tab-bar { display: flex; gap: 6px; margin-bottom: 16px; }
  .tab-btn {
    display: flex;
    align-items: center;
    gap: 7px;
    height: 36px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 600;
    border: 1px solid #d9dfeb;
    border-radius: 6px;
    background: #ffffff;
    color: #7b8397;
    cursor: pointer;
  }
  .tab-btn.is-active { background: #123a71; border-color: #123a71; color: #ffffff; }
  .tab-badge {
    background: #a4472f;
    color: #ffffff;
    border-radius: 10px;
    font-size: 10.5px;
    padding: 1px 6px;
  }
  .tab-btn.is-active .tab-badge { background: #ffffff; color: #123a71; }

  .state-card, .request-card, .blocked-card {
    background: #ffffff;
    border: 1px solid #e4e9f2;
    border-radius: 8px;
    padding: 18px;
    font-size: 13px;
    color: #3c4658;
  }

  .blocked-card { display: flex; gap: 14px; align-items: flex-start; }
  .blocked-icon { font-size: 22px; color: #a4472f; }
  .blocked-title { font-size: 15px; font-weight: 600; margin-bottom: 3px; }
  .blocked-text { color: #7b8397; line-height: 1.5; }

  .roster-note-card {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    background: #eef4ff;
    border: 1px solid #c2d4f0;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 14px;
    font-size: 12.5px;
    line-height: 1.5;
    color: #123a71;
  }
  .roster-note-icon { font-size: 16px; }
  .roster-note-text { flex: 1; }
  .requester-sub { font-size: 11.5px; color: #7b8397; margin-top: 4px; }

  .requester-line {
    font-size: 12.5px;
    color: #3c4658;
    background: #f6f8fc;
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    padding: 9px 12px;
    margin-bottom: 16px;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 14px;
  }
  .form-field { display: flex; flex-direction: column; gap: 5px; }
  .form-field.full { margin-top: 14px; }
  .form-label {
    font-size: 11px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .req { color: #a4472f; }
  .form-input {
    height: 36px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    padding: 0 10px;
    font-size: 13px;
    color: #22293a;
    background: #ffffff;
    font-family: inherit;
  }
  .form-input:focus { outline: none; border-color: #123a71; }
  .form-textarea { height: auto; padding: 8px 10px; resize: vertical; }
  .form-hint { font-size: 11px; color: #7b8397; }
  .form-hint.is-warn { color: #a4472f; }

  .amount-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    margin-top: 14px;
    flex-wrap: wrap;
  }
  .amount-row .form-field { min-width: 150px; }
  .amount-or { font-size: 12px; color: #7b8397; padding-bottom: 10px; }
  .amount-derived {
    font-size: 12.5px;
    color: #123a71;
    background: #eef4ff;
    border: 1px solid #c2d4f0;
    border-radius: 5px;
    padding: 8px 10px;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 18px;
  }
  .cancel-btn, .save-btn, .reject-confirm-btn {
    height: 36px;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 5px;
    cursor: pointer;
    border: 1px solid #d9dfeb;
    background: #ffffff;
    color: #3c4658;
  }
  .save-btn { background: #123a71; border-color: #123a71; color: #ffffff; }
  .preview-btn {
    height: 36px;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 5px;
    cursor: pointer;
    background: #eef4ff;
    border: 1px solid #c2d4f0;
    color: #123a71;
  }
  .preview-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .reject-confirm-btn { background: #a4472f; border-color: #a4472f; color: #ffffff; }
  .cancel-btn:disabled, .save-btn:disabled, .reject-confirm-btn:disabled {
    opacity: 0.5; cursor: not-allowed;
  }

  .inline-error {
    font-size: 12.5px;
    color: #a4472f;
    background: #fdeeea;
    border: 1px solid #f2c9bd;
    border-radius: 5px;
    padding: 9px 11px;
    margin-top: 14px;
  }

  .outcome-card {
    margin-top: 16px;
    border-radius: 6px;
    padding: 12px 14px;
    font-size: 13px;
  }
  .outcome-card.is-approved { background: #e9f7ef; border: 1px solid #b9e3cb; color: #1e7d4f; }
  .outcome-card.is-preview { background: #f6f8fc; border: 1px solid #d9dfeb; color: #3c4658; }
  .outcome-pricing {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    font-size: 12.5px;
    margin: 8px 0 4px;
  }
  .outcome-card.is-pending { background: #eef4ff; border: 1px solid #c2d4f0; color: #123a71; }
  .outcome-head { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
  .outcome-text { line-height: 1.5; }
  .outcome-text.is-warn { color: #7a5b12; margin-top: 6px; }
  .outcome-text.is-ok { margin-top: 6px; }

  .summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(104px, 1fr));
    gap: 8px;
    margin-bottom: 14px;
  }
  .summary-card {
    background: #ffffff;
    border: 1px solid #e4e9f2;
    border-radius: 7px;
    padding: 12px 10px;
    text-align: center;
  }
  .summary-value { font-size: 21px; font-weight: 600; }
  .summary-label {
    font-size: 10.5px;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-top: 2px;
  }
  .summary-card.is-pending .summary-value { color: #123a71; }
  .summary-card.is-approved .summary-value { color: #1e7d4f; }
  .summary-card.is-rejected .summary-value { color: #a4472f; }
  .summary-card.is-warn .summary-value { color: #7a5b12; }

  .filter-row {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;
    align-items: center;
  }
  /* Eight controls on one row need to stay readable rather than each stretching. */
  .filter-row .filter-select { max-width: 220px; }

  .warn-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12.5px;
    line-height: 1.5;
    background: #fff8e6;
    border: 1px solid #f0dca8;
    color: #7a5b12;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 12px;
  }

  .table-wrap {
    background: #ffffff;
    border: 1px solid #e4e9f2;
    border-radius: 8px;
    overflow-x: auto;
  }
  .data-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .data-table th {
    text-align: left;
    font-size: 10.5px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    background: #f6f8fc;
    padding: 9px 10px;
    border-bottom: 1px solid #e4e9f2;
    white-space: nowrap;
  }
  .data-table td {
    padding: 9px 10px;
    border-bottom: 1px solid #f0f3f8;
    color: #3c4658;
    vertical-align: top;
  }
  .data-table th.num, .data-table td.num { text-align: right; }
  .data-table .nowrap { white-space: nowrap; }
  .data-table .wrap-email { overflow-wrap: anywhere; }
  .sub-note { font-size: 11px; color: #7b8397; margin-top: 2px; }

  .pill {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 600;
    border-radius: 10px;
    padding: 2px 8px;
    white-space: nowrap;
  }
  .pill.is-approved { background: #e9f7ef; color: #1e7d4f; }
  .pill.is-rejected { background: #fdeeea; color: #a4472f; }
  .pill.is-pending { background: #eef4ff; color: #123a71; }
  .pill.is-neutral { background: #f1f2f5; color: #7b8397; }
  .pill.is-warn { background: #fff8e6; color: #7a5b12; }

  .audit-search { min-width: 160px; cursor: text; }
  .audit-count { font-size: 12.5px; color: #7b8397; margin-bottom: 10px; }
  .audit-notes { font-size: 12px; color: #3c4658; line-height: 1.5; max-width: 420px; }
  .audit-pager {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
  }

  .actions-cell { white-space: nowrap; }
  .link-btn, .approve-btn, .reject-btn {
    font-size: 11px;
    font-weight: 600;
    border-radius: 4px;
    padding: 3px 9px;
    cursor: pointer;
    margin-right: 4px;
    border: 1px solid #d9dfeb;
    background: #ffffff;
    color: #3c4658;
  }
  .approve-btn { background: #f0fbf5; border-color: #b8e5cc; color: #1b7a47; }
  .reject-btn { background: #fdeeea; border-color: #f2c9bd; color: #a4472f; }
  .link-btn:disabled, .approve-btn:disabled, .reject-btn:disabled {
    opacity: 0.5; cursor: not-allowed;
  }

  .detail-row td { background: #fbfcfe; }
  .detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 18px;
    padding: 6px 2px;
  }
  .detail-title {
    font-size: 10.5px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 5px;
  }
  .detail-line { font-size: 12px; color: #3c4658; line-height: 1.6; overflow-wrap: anywhere; }
  .detail-line.is-warn { color: #a4472f; }
  .detail-line code {
    background: #f1f2f5;
    border-radius: 3px;
    padding: 1px 4px;
    font-size: 11px;
  }
  .event-when { color: #7b8397; margin-right: 6px; }
  .event-notes { color: #7b8397; padding-left: 10px; }

  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(18, 24, 38, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }
  .modal-card {
    background: #ffffff;
    border-radius: 9px;
    width: 100%;
    max-width: 520px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px;
    border-bottom: 1px solid #e4e9f2;
  }
  .modal-title { font-size: 16px; font-weight: 600; margin: 0; }
  .modal-subtitle { font-size: 12.5px; color: #7b8397; margin-top: 2px; }
  .close-btn {
    border: none;
    background: none;
    font-size: 15px;
    color: #7b8397;
    cursor: pointer;
    line-height: 1;
  }
  .modal-body { padding: 16px 18px 18px; overflow-y: auto; }
  .modal-footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
  }
  .decision-summary {
    font-size: 13px;
    background: #f6f8fc;
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 12px;
  }
  .decision-note-banner {
    font-size: 12.5px;
    background: #eef4ff;
    border: 1px solid #c2d4f0;
    color: #123a71;
    border-radius: 6px;
    padding: 9px 11px;
    margin-bottom: 12px;
  }

  /* ── Polly intake diagnostics ──────────────────────────────────────────── */
  .diag-explainer {
    font-size: 12.5px;
    color: #475569;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 9px 11px;
    margin-bottom: 10px;
  }
  .mode-pill {
    display: inline-block;
    padding: 1px 7px;
    border-radius: 10px;
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
  }
  .mode-pill.is-dry {
    background: #fff7ed;
    color: #9a3412;
    border: 1px solid #fed7aa;
  }
  .mode-pill.is-live {
    background: #f0fbf5;
    color: #1b7a47;
    border: 1px solid #b8e5cc;
  }
  .diag-sub {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
    word-break: break-word;
  }
  .diag-action {
    font-size: 11.5px;
    font-weight: 700;
    color: #1e293b;
  }
  .diag-flag {
    display: inline-block;
    margin-top: 3px;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
  }
  .diag-flag.is-warn {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  }
  .diag-flag.is-sent {
    background: #eef4ff;
    color: #123a71;
    border: 1px solid #c2d4f0;
  }
  .diag-message {
    font-size: 11.5px;
    color: #475569;
    max-width: 320px;
  }

  /* ── Concession approvers importer ──────────────────────────────────── */
  .approvers-btn {
    height: 38px;
    width: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #7b8397;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;
  }
  .approvers-btn:hover { background: #eef4ff; border-color: #c2d4f0; color: #123a71; }
  .approvers-btn .fa-duotone,
  .approvers-btn .fa-duotone::before,
  .approvers-btn .fa-duotone::after {
    --fa-primary-opacity: 1;
    --fa-secondary-opacity: 1;
  }

  .approver-modal-card { max-width: 1080px; width: 96vw; }
  .approver-modal-body { max-height: 78vh; overflow-y: auto; }

  .approver-current {
    font-size: 13px;
    color: #3c4658;
    background: #f6f8fc;
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 14px;
  }
  .approver-current-sub { color: #7b8397; }

  .approver-drop {
    position: relative;
    border: 2px dashed #d9dfeb;
    border-radius: 8px;
    background: #fbfcfe;
    padding: 22px 16px 18px;
    text-align: center;
    transition: all 0.15s ease;
  }
  .approver-drop.is-over { border-color: #123a71; background: #eef4ff; }
  .approver-drop.has-file { border-style: solid; border-color: #c2d4f0; background: #ffffff; }
  .approver-file-input { display: none; }
  .approver-drop-icon { font-size: 26px; color: #7b8397; margin-bottom: 8px; display: block; }
  .approver-drop.has-file .approver-drop-icon { color: #1e7d4f; }
  .approver-drop-title { font-size: 14px; font-weight: 600; color: #22293a; }
  .approver-drop-hint { font-size: 12px; color: #7b8397; margin-top: 2px; }
  .approver-browse-btn {
    margin-top: 12px;
    height: 32px;
    padding: 0 14px;
    font-size: 13px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #3c4658;
    cursor: pointer;
  }
  .approver-browse-btn:hover:not(:disabled) { background: #eef4ff; border-color: #c2d4f0; }
  .approver-browse-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .approver-busy {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #3c4658;
    padding: 14px 0 4px;
  }

  .approver-result-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    border-radius: 6px;
    padding: 10px 12px;
    margin: 14px 0 12px;
  }
  .approver-result-banner.is-preview { background: #eef4ff; border: 1px solid #c2d4f0; color: #123a71; }
  .approver-result-banner.is-committed { background: #e9f7ef; border: 1px solid #b9e3cb; color: #1e7d4f; }

  .approver-warn-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12.5px;
    line-height: 1.5;
    background: #fff8e6;
    border: 1px solid #f0dca8;
    color: #7a5b12;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 14px;
  }

  .approver-counts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
    gap: 8px;
    margin-bottom: 14px;
  }
  .approver-count-card {
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    background: #ffffff;
    padding: 10px 8px;
    text-align: center;
  }
  .approver-count-value { font-size: 20px; font-weight: 600; color: #22293a; }
  .approver-count-label {
    font-size: 10.5px;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-top: 2px;
  }
  .approver-count-card.is-new .approver-count-value { color: #1e7d4f; }
  .approver-count-card.is-changed .approver-count-value { color: #123a71; }
  .approver-count-card.is-off .approver-count-value { color: #a4472f; }

  .approver-section-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 11px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 16px 0 8px;
  }
  .approver-toggle {
    border: none;
    background: none;
    color: #123a71;
    font-size: 11px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    cursor: pointer;
    padding: 0;
  }
  .approver-toggle:hover { text-decoration: underline; }

  .approver-table-wrap { overflow-x: auto; border: 1px solid #e4e9f2; border-radius: 6px; }
  .approver-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .approver-table th {
    text-align: left;
    font-size: 10.5px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    background: #f6f8fc;
    padding: 8px 10px;
    border-bottom: 1px solid #e4e9f2;
    white-space: nowrap;
  }
  .approver-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #f0f3f8;
    color: #3c4658;
  }
  .approver-table tbody tr:last-child td { border-bottom: none; }
  .approver-table th.num, .approver-table td.num { text-align: right; }
  .approver-table td.is-zero { color: #a4472f; font-weight: 600; }

  .approver-pill {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 600;
    border-radius: 10px;
    padding: 2px 8px;
  }
  .approver-pill.is-yes { background: #e9f7ef; color: #1e7d4f; }
  .approver-pill.is-no { background: #f1f2f5; color: #7b8397; }

  .approver-table-note {
    font-size: 11.5px;
    line-height: 1.5;
    color: #7b8397;
    margin-top: 6px;
  }

  .approver-warn-list, .approver-change-list {
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    max-height: 260px;
    overflow-y: auto;
  }
  .approver-warn-row {
    display: grid;
    grid-template-columns: 64px minmax(140px, 200px) 1fr;
    gap: 10px;
    font-size: 12px;
    padding: 7px 10px;
    border-bottom: 1px solid #f0f3f8;
  }
  .approver-warn-row:last-child { border-bottom: none; }
  .approver-warn-row-loc { color: #7b8397; }
  .approver-warn-row-who { color: #3c4658; overflow-wrap: anywhere; }
  .approver-warn-row-msg { color: #7a5b12; }
  .approver-warn-more {
    font-size: 11.5px;
    color: #7b8397;
    padding: 8px 10px;
    background: #f6f8fc;
  }

  .approver-change-row {
    display: grid;
    grid-template-columns: 92px minmax(160px, 220px) 120px 1fr;
    gap: 10px;
    align-items: start;
    font-size: 12px;
    padding: 7px 10px;
    border-bottom: 1px solid #f0f3f8;
  }
  .approver-change-row:last-child { border-bottom: none; }
  .approver-change-tag {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-radius: 10px;
    padding: 2px 7px;
    text-align: center;
  }
  .approver-change-tag.tag-new { background: #e9f7ef; color: #1e7d4f; }
  .approver-change-tag.tag-changed { background: #eef4ff; color: #123a71; }
  .approver-change-tag.tag-deactivating { background: #fdeeea; color: #a4472f; }
  .approver-change-tag.tag-reactivating { background: #fff8e6; color: #7a5b12; }
  .approver-change-who { color: #22293a; display: flex; flex-direction: column; overflow-wrap: anywhere; }
  .approver-change-email { font-size: 11px; color: #7b8397; }
  .approver-change-div { color: #7b8397; }
  .approver-change-fields { display: flex; flex-direction: column; gap: 2px; }
  .approver-change-field { color: #3c4658; overflow-wrap: anywhere; }
  .approver-change-field em { color: #7b8397; font-style: normal; }
</style>
